// src/components/ErrorBoundary/ErrorBoundary.js
import React from "react";
import "./errorboundary.css";
import { SUPPORT_EMAIL } from "../../config/publicLinks";
/**
 * Enterprise ErrorBoundary
 *
 * Usage:
 * <ErrorBoundary resetKeys={[location.pathname]}>
 *   <App />
 * </ErrorBoundary>
 *
 * Optional props:
 *  - fallback?: ({ error, reset, reload, isChunkError }) => ReactNode
 *  - onError?: (error: Error, info: React.ErrorInfo) => void
 *  - resetKeys?: any[]  (when these change, boundary resets)
 *  - supportEmail?: string
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, resetCount: 0 };
    this._mounted = false;
    this._lastResetKeys = props.resetKeys || [];
    this._focusRef = React.createRef();
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Always log locally
    console.error("[ErrorBoundary] Caught error:", error, info);

    // Optional hook for Sentry/LogRocket/etc.
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError(error, info);
      } catch (e) {
        console.error("[ErrorBoundary] onError handler failed:", e);
      }
    }
  }

  componentDidMount() {
    this._mounted = true;
    if (this.state.hasError) this.focusAlert();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  componentDidUpdate(prevProps, prevState) {
    // Focus the alert once we enter error state
    if (!prevState.hasError && this.state.hasError) {
      this.focusAlert();
    }

    // Reset when resetKeys change
    const prevKeys = prevProps.resetKeys || [];
    const nextKeys = this.props.resetKeys || [];

    if (prevKeys.length !== nextKeys.length) {
      // treat length change as reset
      if (this.state.hasError) this.reset();
      return;
    }

    const changed = nextKeys.some((k, i) => !Object.is(k, prevKeys[i]));
    if (changed && this.state.hasError) {
      this.reset();
    }
  }

  focusAlert() {
    // Focus for accessibility (screen readers + keyboard)
    try {
      requestAnimationFrame(() => this._focusRef.current?.focus?.());
    } catch {
      // ignore
    }
  }

  isChunkError = (err) => {
    const msg = String(err?.message || err || "");
    return (
      msg.includes("Loading chunk") ||
      msg.includes("ChunkLoadError") ||
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Importing a module script failed")
    );
  };

  reset = () => {
    // resetCount forces a remount of children subtree (actual recovery)
    this.setState((s) => ({
      hasError: false,
      error: null,
      resetCount: s.resetCount + 1,
    }));
  };

  reloadPage = () => {
    // Full reload: best recovery for chunk mismatch after deploy
    window.location.reload();
  };

  render() {
    const { hasError, error, resetCount } = this.state;

    if (hasError) {
      const supportEmail = this.props.supportEmail || SUPPORT_EMAIL;
      const chunkErr = this.isChunkError(error);

      if (typeof this.props.fallback === "function") {
        return this.props.fallback({
          error,
          reset: this.reset,
          reload: this.reloadPage,
          isChunkError: chunkErr,
        });
      }

      return (
        <div className="eb">
          <div
            className="eb-card"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            tabIndex={-1}
            ref={this._focusRef}
          >
            <div className="eb-icon" aria-hidden="true">
              ⛔
            </div>

            <h1 className="eb-title">
              {chunkErr ? "Update required" : "Something went wrong"}
            </h1>

            <p className="eb-subtitle">
              {chunkErr
                ? "A new version of the app is available. Reload to continue."
                : "Try reloading this view. If it keeps happening, contact support."}
            </p>

            {process.env.NODE_ENV !== "production" && (
              <div className="eb-dev">
                {error?.message ? <pre className="eb-debug">{error.message}</pre> : null}
                {error?.stack ? <pre className="eb-debug eb-stack">{error.stack}</pre> : null}
              </div>
            )}

            <div className="eb-actions">
              {!chunkErr && (
                <button className="eb-btn eb-btn-primary" onClick={this.reset} type="button">
                  Try again
                </button>
              )}

              <button className="eb-btn" onClick={this.reloadPage} type="button">
                Reload app
              </button>

              <a className="eb-btn eb-btn-ghost" href={`mailto:${supportEmail}`}>
                Contact support
              </a>
            </div>
          </div>
        </div>
      );
    }

    // ✅ Important: key forces subtree remount after resetCount increments
    return <React.Fragment key={resetCount}>{this.props.children}</React.Fragment>;
  }
}

export default ErrorBoundary;
